import React, { useEffect } from 'react'

import TablePaginationActions from './TablePaginationActions'
import TableToolbar from './TableToolbar'
import {
    useGlobalFilter,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
    useAsyncDebounce,
    
} from 'react-table'
import {
    Checkbox, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
    TablePagination, Table, Typography, Box,
    TextField,
    Radio
} from '@mui/material'
import ReactLoading from 'react-loading';
import { Colors } from '../../styles/theme'
import { DEFAULT_PAGE_SIZE, isEmptyObject } from '../../utils'
import { Block, CheckCircle } from '@mui/icons-material'
import ColumnHeaderWithFilter from './ColumnHeaderWithFilter'
import TableActions from './TableActions'

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef()
        const resolvedRef = ref || defaultRef

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        return (
            <>
                <Checkbox ref={resolvedRef} {...rest} />
            </>
        )
    }
)

// Create an editable cell renderer
const EditableCell = ({
    value: initialValue,
    //   row: { index },
    //   column: { id },
    //   updateMyData, // This is a custom function that we supplied to our table instance
}) => {

    // We need to keep and update the state of the cell normally
    const [value, setValue] = React.useState(initialValue)

    //   const onChange = e => {
    //     setValue(e.target.value)
    //   }

    // We'll only update the external data when the input is blurred
    //   const onBlur = () => {
    //     updateMyData(index, id, value)
    //   }

    // If the initialValue is changed externall, sync it up with our state
    React.useEffect(() => {

        setValue(initialValue)
    }, [initialValue])

    return (
        <>{value}</>
        // <Typography sx={{ textTransform: 'capitalize' }}>{value}</Typography>
        // <TextField
        //     size='small'
        //     InputProps={{
        //         notched: false
        //     }}
        //   style={inputStyle}
        //   value={value}
        //   onChange={onChange}
        //   onBlur={onBlur}
        // />
    )
}

// EditableCell.propTypes = {
//   cell: PropTypes.shape({
//     value: PropTypes.any.isRequired,
//   }),
//   row: PropTypes.shape({
//     index: PropTypes.number.isRequired,
//   }),
//   column: PropTypes.shape({
//     id: PropTypes.number.isRequired,
//   }),
//   updateMyData: PropTypes.func.isRequired,
// }

// Set our editable cell renderer as the default Cell renderer
const defaultColumn = {
    Cell: EditableCell,
}


export const ColumnFilter = ({ column }) => {
    const { filterValue, setFilter } = column;

    return (
        <TextField
            value={filterValue || ''}
            onChange={e => setFilter(e.target.value)}
            size="small"
            placeholder={`Search...`}
            sx={{ mt: 1 }}
        />
    );
};

const EnhancedTable = ({
    columns,
    data,
    totalRowCount,
    updateMyData,
    deleteRecordHandler,
    onRowSelected,
    addRecordHandler,
    actions = [],
    fetchData,
    loading,
    pageCount: controlledPageCount,
    selectionMode = 'none', // 'none', 'single', 'multi'
    onRowSelect,  // Callback for row selection
    showStatusColumn = true, // Controls auto-added Status column
    // New: custom actions that show when selection > 0
    selectedToolbarActions = [],
}) => {
    const {
        getTableProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        setPageSize,
        preGlobalFilteredRows,
        setGlobalFilter,
    // nextPage, // removed unused variable
        state: { pageIndex, pageSize, selectedRowIds,filters, globalFilter, sortBy },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }, // Pass our hoisted table state
            pageCount: controlledPageCount,
            manualFilters: true,
            manualGlobalFilter: true,
            manualPagination: true, // Tell the usePagination
            // hook that we'll handle our own data fetching
            // This means we'll also have to provide our own pageCount.
            autoResetPage: false,
            manualSortBy: true,     // we do sorting on the server
            // updateMyData isn't part of the API, but
            // anything we put into these options will
            // automatically be available on the instance.
            // That way we can call this function from our
            // cell renderer!
            updateMyData,
            enableRowSelect: selectionMode !== 'none',
            selectMode: selectionMode,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            hooks.allColumns.push(columns => {
                const enhancedColumns = [
                    // Only add selection column if selection is enabled
                    ...(selectionMode !== 'none' ? [{
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }) => (
                            <div>
                                {selectionMode === 'multi' && (
                                    <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                                )}
                            </div>
                        ),
                        Cell: ({ row }) => (
                            <div>
                                {selectionMode === 'multi' ? (
                                    <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                                ) : (
                                    <Radio
                                        checked={row.isSelected}
                                        onChange={() => handleRowClick(row, page)}
                                    />
                                )}
                            </div>
                        ),
                    }] : []),
                    ...columns
                ];
                // Check if status column already exists in the provided columns
                const hasStatusColumn = columns.some(col => col.Header === 'Status');

                // Only add status column if it's not already present
                if (showStatusColumn && !hasStatusColumn) {
                    enhancedColumns.push({
                        id: 'status',
                        Header: () => `Status`,
                        Cell: ({ row }) => row.original.status === 'active' ?
                            <CheckCircle color='success' /> :
                            <Block color='error' />
                    });
                }

                // Action column
                enhancedColumns.push({
                    id: 'action',
                    Header: () => <div></div>,
                    Cell: ({ row }) => <TableActions row={row} actions={actions} />
                });
                return enhancedColumns;
            })
        }
    )

    const fetchDataDebounced = useAsyncDebounce(fetchData, 500);

    // Create selected rows array for consumers/toolbar
    const selectedRows = React.useMemo(() => {
        if (!selectedRowIds) return [];
        return Object.keys(selectedRowIds)
            .map(index => data[parseInt(index, 10)])
            .filter(Boolean);
    }, [selectedRowIds, data]);

    // Listen for changes in pagination and use the state to fetch our new data
    useEffect(() => {
        fetchDataDebounced({ pageIndex, pageSize, filters, sortBy });
    }, [fetchDataDebounced, pageIndex, pageSize, filters, sortBy]);

    useEffect(() => {
        if (!isEmptyObject(selectedRowIds) && onRowSelected) {
            const selected = data.filter((_, i) => Object.keys(selectedRowIds).map(x => parseInt(x, 10)).includes(i)).map(x => x);
            onRowSelected(selected);
        }
    }, [selectedRowIds, data, onRowSelected]);

    /**
     * on filter we want to reset the page to 0
     */
    useEffect(() => {
        gotoPage(0);
    }, [filters, gotoPage, sortBy]);

    const handleChangePage = (event, newPage) => {
        gotoPage(newPage);
    }

    const handleChangeRowsPerPage = event => {
        setPageSize(Number(event.target.value))
    }

    // const removeByIndex = (array, indexs) =>
    //     array.filter((_, i) => indexs.includes(i)) // unused helper removed

    const _deleteRecordHandler = event => {
        const toDelete = data.filter((_, i) => Object.keys(selectedRowIds).map(x => parseInt(x, 10)).includes(i)).map(x => x.id);
        deleteRecordHandler(toDelete);
    }

    const syncDataHandler = () => fetchDataDebounced({ pageIndex, pageSize, globalFilter, sortBy });

    // Handle multi-select mode
    useEffect(() => {
        if (selectionMode === 'multi' && onRowSelect && !isEmptyObject(selectedRowIds)) {
            const selectedRows = Object.keys(selectedRowIds)
                .map(index => data[parseInt(index, 10)])
                .filter(Boolean);
            onRowSelect(selectedRows);
        }
    }, [selectedRowIds, data, selectionMode, onRowSelect]);

    const deselectAllRows = (rows) => {
        rows.forEach(row => {
            if (row.isSelected) {
                row.toggleRowSelected(false);
            }
        });
    };

    const clearSelection = () => {
        deselectAllRows(page);
    };

    // Add click handler for row selection
    const handleRowClick = (row, page) => {
        if (selectionMode === 'single') {
            // Deselect all rows first
            deselectAllRows(page);

            // Then select the clicked row
            row.toggleRowSelected(true);

            if (onRowSelect) {
                onRowSelect(row.original);
            }
        }
    };

    // Render the UI for your table
    return (
        <TableContainer
                sx={{
                    // Critical: Explicitly enable vertical scrolling to fix Safari scroll issues
                    // Safari sometimes fails to recalculate scroll dimensions when async content loads
                    overflowY: 'auto',
                    
                    // Enable momentum/smooth scrolling on iOS and Safari
                    WebkitOverflowScrolling: 'touch',
                    
                    // Establish positioning context for the scroll container
                    position: 'relative',
                    
                    // Custom scrollbar styling for better visibility
                    '&::-webkit-scrollbar': {
                        width: 6,
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.3)',
                        borderRadius: 4,
                    },
                }}        
        >
            <TableToolbar
                numSelected={Object.keys(selectedRowIds).length}
                deleteRecordHandler={deleteRecordHandler && _deleteRecordHandler}
                addRecordHandler={addRecordHandler}
                preGlobalFilteredRows={preGlobalFilteredRows}
                setGlobalFilter={setGlobalFilter}
                globalFilter={globalFilter}
                syncData={syncDataHandler}
                // New props for custom actions
                selectedRows={selectedRows}
                selectedToolbarActions={selectedToolbarActions}
                onClearSelection={clearSelection}
            />

            <Table {...getTableProps()} size='small'>
                <TableHead>
                    {headerGroups.map(headerGroup => (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <TableCell
                                {...(column.id === 'selection'
                                    ? column.getHeaderProps()
                                    : column.getHeaderProps(column.getSortByToggleProps()))}
                            >
                                {column.id === 'selection' || column.id === 'action' || column.id === 'status' ? (
                                    column.render('Header')
                                ) : (
                                    <ColumnHeaderWithFilter column={column} />
                                )}
                            </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {
                        loading ? <TableRow><TableCell colSpan={1000}>
                            <Box display={'flex'} justifyContent="center" alignItems={"center"}>
                                <ReactLoading type='bubbles' color={Colors.primary} />
                            </Box>
                        </TableCell></TableRow> :
                            page.length > 0 ?
                                page.map((row, i) => {
                                    prepareRow(row)
                                    return (
                                        <TableRow {...row.getRowProps()} 
                                        onClick={() => handleRowClick(row, page)}
                                            
                                            sx={{
                                                cursor: selectionMode !== 'none' ? 'pointer' : 'default',
                                                bgcolor: row.isSelected ? 'action.selected' : 'inherit',
                                                '&:hover': {
                                                    bgcolor: selectionMode !== 'none' ? 'action.hover' : 'inherit',
                                                },
                                            }}>
                                            {row.cells.map(cell => {
                                                return (
                                                    <TableCell {...cell.getCellProps()}  sx={{ textTransform: 'capitalize', '&. MuiTypography-body1': {
                                                    fontSize: '0.25rem',
                                                },}}>
                                                        {cell.render('Cell')}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                    )
                                }) :
                                <TableRow><TableCell colSpan={1000}>
                                    <Box display={'flex'} justifyContent="center" alignItems={"center"}>
                                        <Typography>No records found</Typography>
                                    </Box>
                                </TableCell></TableRow>
                    }
                </TableBody>

                <TableFooter>
                    <TableRow sx={{ width: '100%'}}>
                        <TableCell colSpan={1000} sx={{ p: 0, border: 0 }}>
                            <TablePagination
                                rowsPerPageOptions={[
                                    DEFAULT_PAGE_SIZE,
                                    50,
                                    100,
                                    200
                                    //500
                                    //{ label: 'All', value: data.length },
                                ]}
                                count={totalRowCount}
                                rowsPerPage={pageSize}
                                page={pageIndex}
                                SelectProps={{
                                    inputProps: { 'aria-label': 'rows per page' },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    '& .MuiToolbar-root': {
                                        justifyContent: 'center'
                                    }
                                }}
                            />
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>

        </TableContainer>
    )
}

// EnhancedTable.propTypes = {
//   columns: PropTypes.array.isRequired,
//   data: PropTypes.array.isRequired,
//   updateMyData: PropTypes.func.isRequired,
//   setData: PropTypes.func.isRequired,
//   skipPageReset: PropTypes.bool.isRequired,
// }

export default EnhancedTable